import { Injectable, expectsInstance, EventEmitter } from "../../helpers";
import { AbstractAuthProvider } from "./abstractAuthProvider.service";

import moment from "moment";

/**
 * @class AuthService
 * @property {Scope} $rootScope
 * @property {AbstractAuthProvider} AuthProvider
 */
export class AuthService extends Injectable {
  static get $inject() {
    return [
      "$rootScope",
      "AuthProvider",
      "AclService",
      "ScheduledMaintenancePmsApiService",
      "$uibModal",
      "$sce",
      "$cookies",
      "$location",
      "AppPropertyFilterService",
      "$interval",
    ];
  }

  get EVENT_REQUEST() {
    return "request";
  }

  get EVENT_REQUEST_SUCCESS() {
    return "request_success";
  }

  get EVENT_REQUEST_FAILED() {
    return "request_failed";
  }

  get EVENT_REVOKE() {
    return "revoke";
  }

  get EVENT_TOKEN_REFRESHED() {
    return "token_refreshed";
  }

  get EVENT_TOKEN_REFRESH_FAILED() {
    return "token_refresh_failed";
  }

  /**
   * @type {*|null}
   */
  get identifier() {
    return this._identifier;
  }

  /**
   * @returns {Promise<*>}
   */
  get user() {
    return new Promise((resolve, reject) => {
      if (this._user) {
        resolve(this._user);
      } else if (this.identifier) {
        this.AuthProvider.user(this.identifier).then(
          (user) => {
            this.setUser(user);

            resolve(user);
          },
          () => {
            this.clearData();
            reject();
          }
        );
      } else {
        reject();
      }
    });
  }

  /**
   * @protected
   */
  readIdentifierFromStorage() {
    const identifier = localStorage.getItem(
      this.isBillingPortal() ? this._billingPortalStorageKey : this._storageKey
    );
    this._identifier = identifier ? JSON.parse(identifier) : null;

    // Setup token refresh if we have a valid identifier with a token
    if (this._identifier && this._identifier.token) {
      this.setupTokenRefresh();
    }
  }

  /**
   * @protected
   */
  clearData() {
    // Clear token refresh interval if it exists
    if (this._tokenRefreshInterval) {
      this.$interval.cancel(this._tokenRefreshInterval);
      this._tokenRefreshInterval = null;
    }

    // this.$cookies.remove(this.isBillingPortal() ? this._billingPortalStorageKey : this._storageKey);
    localStorage.removeItem(
      this.isBillingPortal() ? this._billingPortalStorageKey : this._storageKey
    );

    this._identifier = null;
    this._user = null;
    this.$rootScope.$user = null;
    this.$rootScope.open_cash_drawers = [];
    this.$rootScope.closed_cash_drawers = [];

    localStorage.removeItem("selectedPropertyData");
    this.AppPropertyFilterService.clear();
  }

  /**
   * Checks if the current token is about to expire and refreshes it if needed
   * @protected
   */
  checkAndRefreshToken() {
    if (!this._identifier || !this._identifier.token) {
      return;
    }

    // Get expiration time and check if it's close to expiring (within 5 minutes)
    const expiredAt = moment(this._identifier.expired_at);
    const now = moment();
    const timeToExpiry = expiredAt.diff(now, "minutes");

    // If token will expire within 5 minutes and we have a refresh token, refresh it
    if (timeToExpiry <= 5 && this._identifier.token) {
      this.refreshToken();
    }
  }

  /**
   * Refreshes the access token using the refresh token
   * @returns {Promise<void>}
   */
  refreshToken() {
    if (!this._identifier || !this._identifier.token) {
      return Promise.reject("No refresh token available");
    }

    return this.AuthProvider.refreshToken()
      .then(({ identifier }) => {
        // Update the identifier with the new token
        this.setIdentifier(identifier, true);
        this.events.emit(this.EVENT_TOKEN_REFRESHED);
        return identifier;
      })
      .catch((error) => {
        this.events.emit(this.EVENT_TOKEN_REFRESH_FAILED, error);
        // If refresh fails, user will need to log in again
        if (error.status === 401) {
          this.clearData();
        }
        return Promise.reject(error);
      });
  }

  /**
   * @param {*} user
   * @protected
   */
  setUser(user) {
    if (user) {
      this.AclService.flushRoles();

      let aclData = {};

      const roles = user.isMabeetStaff()
        ? user.roles
        : user.roles.filter(
            (r) =>
              user.active_properties &&
              user.active_properties.includes(r.property_id)
          );

      for (let role of roles) {
        aclData[role.id] = [];

        this.AclService.attachRole(role.id);

        for (let permission of role.permissions) {
          aclData[role.id].push(permission.name);
        }
      }

      if (user.permissions.length) {
        aclData["direct-permissons"] = [];

        this.AclService.attachRole("direct-permissons");

        for (let permission of user.permissions) {
          aclData["direct-permissons"].push(permission.name);
        }
      }

      if (
        (user.isBusinessOwner() || user.isPropertyStaff()) &&
        this.$location.host() !== this.rootDomain
      ) {
        this.ScheduledMaintenancePmsApiService.currentAnnouncements().then(
          (models) => {
            if (models.length > 0) {
              this.showAnnouncements(models);
            }
          }
        );
      }

      this.AclService.setAbilities(aclData);
    }

    this._user = user;

    this.$rootScope.$user = user;
  }

  showAnnouncements(models, index = 0) {
    this.$uibModal
      .open({
        component: "announcementModal",
        resolve: {
          title: () =>
            this.$rootScope.$lang != "en"
              ? models[index].title_ar
              : models[index].title,
          content: () =>
            this.$sce.trustAsHtml(
              this.$rootScope.$lang != "en"
                ? models[index].content_ar
                : models[index].content
            ),
        },
      })
      .result.then((result) => {
        if (result) {
          this.ScheduledMaintenancePmsApiService.hideAnnouncement(
            models[index]
          );
        }

        if (index < models.length - 1) {
          index += 1;

          this.showAnnouncements(models, index);
        }
      });
  }

  /**
   * @param {*} identifier
   * @param {boolean} [remember=false]
   * @protected
   */
  setIdentifier(identifier, remember = false) {
    if (remember) {
      localStorage.setItem(
        this.isBillingPortal()
          ? this._billingPortalStorageKey
          : this._storageKey,
        JSON.stringify(identifier)
      );
    }
    this._identifier = identifier;

    // Setup token refresh when identifier is set
    if (identifier && identifier.token) {
      this.setupTokenRefresh();
    }
  }

  /**
   * @protected
   */
  billingPortalAuth() {
    return this.AuthProvider.requestBillingPortalAuth().then(
      ({ identifier }) => {
        localStorage.setItem(
          this._billingPortalStorageKey,
          JSON.stringify(identifier)
        );
      }
    );
  }

  /**
   * @param {Scope} $rootScope
   * @param {AbstractAuthProvider} AuthProvider
   */
  constructor(
    $rootScope,
    AuthProvider,
    AclService,
    ScheduledMaintenancePmsApiService,
    $uibModal,
    $sce,
    $cookies,
    $location,
    AppPropertyFilterService,
    $interval
  ) {
    expectsInstance(AbstractAuthProvider, AuthProvider);

    super($rootScope, AuthProvider);

    /**
     * @type {string}
     * @protected
     */
    this._storageKey = "auth_storage";

    /**
     * @type {string}
     * @protected
     */
    this._billingPortalStorageKey = "billing_auth_storage";

    /**
     * @type {*|null}
     * @protected
     */
    this._identifier = null;

    /**
     * @type {*|null}
     * @protected
     */
    this._user = null;

    /**
     * @type {Object|null}
     * @protected
     */
    this._tokenRefreshInterval = null;

    /**
     * @type {Scope}
     * @protected
     */
    this.$rootScope = $rootScope;

    this.AclService = AclService;

    this.ScheduledMaintenancePmsApiService = ScheduledMaintenancePmsApiService;

    this.$uibModal = $uibModal;

    this.$sce = $sce;

    this.$cookies = $cookies;

    this.$location = $location;

    this.AppPropertyFilterService = AppPropertyFilterService;

    this.$interval = $interval;

    this.rootDomain = this.$rootScope.hosts.HOST_URL.split(":")[0];

    this.billingPortalDomain = this.$rootScope.hosts.BILLING_URL.split(":")[0];

    /**
     * @type {EventEmitter}
     */
    this.events = new EventEmitter($rootScope, "auth.");

    this.setUser(null);
    this.readIdentifierFromStorage();
  }

  /**
   * @param {Object} credentials
   * @param {boolean} [remember=false]
   * @returns {Promise<void>}
   */
  request(credentials, remember = true) {
    this.clearData();

    this.events.emit(this.EVENT_REQUEST);

    return this.AuthProvider.request(credentials)
      .then(({ identifier, user = null, otp_sent }) => {
        if (otp_sent) {
          return this.$uibModal
            .open({
              component: "verifyOtpModal",
              resolve: {
                identifier: () => identifier,
                user: () =>  user
              },
            })
            .result.then((result) => {
              this.setIdentifier(identifier, remember);

              if (!user) {
                this.user.then(() =>
                  this.events.emit(this.EVENT_REQUEST_SUCCESS)
                );
              } else {
                this.setUser(user);

                this.events.emit(this.EVENT_REQUEST_SUCCESS);
              }
            })
        } else {
          this.setIdentifier(identifier, remember);

          if (!user) {
            this.user.then(() => this.events.emit(this.EVENT_REQUEST_SUCCESS));
          } else {
            this.setUser(user);

            this.events.emit(this.EVENT_REQUEST_SUCCESS);
          }
        }
      })
      .catch((reason) => {
        this.events.emit(this.EVENT_REQUEST_FAILED, reason);

        return Promise.reject(reason);
      });
  }

  /**
   * @returns {Promise<void>}
   */
  revoke(param = {}) {
    return new Promise((resolve, reject) => {
      if (!this.identifier) {
        this.clearData();
        resolve();
      } else {
        const params = Object.assign(param, { identifier: this.identifier });

        this.AuthProvider.revoke(params).then(() => {
          this.clearData();

          this.events.emit(this.EVENT_REVOKE);
          resolve();
        }, reject);
      }
    });
  }

  isBillingPortal() {
    return this.$location.host() === this.billingPortalDomain;
  }

  /**
   * Sets up the token refresh mechanism to automatically refresh before expiration
   * @protected
   */
  setupTokenRefresh() {
    // Cancel any existing interval
    if (this._tokenRefreshInterval) {
      this.$interval.cancel(this._tokenRefreshInterval);
    }

    // Check for token expiration every minute
    const interval = 55 * 60 * 1000;
    this._tokenRefreshInterval = this.$interval(() => {
      this.checkAndRefreshToken();
    }, interval);
  }
}



// WEBPACK FOOTER //
// ./src/auth/services/auth.service.js